// Protocolo do Hábito — a área de estudo.
//
// O usuário precisa VER por que o programa é assim. Não é o PDF colado numa
// tela: é o método explicado com o que cada decisão tem de evidência atrás,
// incluindo o que a evidência NÃO sustenta.
//
// Regra de honestidade que governa este arquivo: nenhum número aparece sem a
// sua força. Onde a evidência é fraca, está escrito que é fraca. Onde não
// existe estudo, está escrito que não existe.

import React, { useEffect, useState } from 'react';
import { TopBar, Toggle, BookCover } from '../common';
import { Icon } from '../icons';
import { EstadoHabito, habitoApi, ItemDaFila } from './api';
import { EscolherLivro } from './livro';
import { AuthSession } from '../../types';

const serif = 'Instrument Serif, Georgia, serif';

const eyebrow: React.CSSProperties = {
  fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase',
  color: 'var(--ink-3)', fontWeight: 600,
};

const h2: React.CSSProperties = {
  fontFamily: serif, fontSize: 27, lineHeight: 1.14, color: 'var(--ink)', margin: '8px 0 12px',
};

const p: React.CSSProperties = {
  fontSize: 14.8, lineHeight: 1.62, color: 'var(--ink-2)', margin: '0 0 12px',
};

const Secao: React.FC<{ titulo: string; sobre: string; children: React.ReactNode }> = ({ titulo, sobre, children }) => (
  <div style={{ padding: '28px 20px 0' }}>
    <div className="mono" style={eyebrow}>{sobre}</div>
    <h2 style={h2}>{titulo}</h2>
    {children}
  </div>
);

const Cartao: React.FC<{ children: React.ReactNode; escuro?: boolean }> = ({ children, escuro }) => (
  <div style={{
    background: escuro ? '#1A1614' : 'var(--paper-2)',
    border: `1px solid ${escuro ? 'rgba(245,241,234,0.10)' : 'var(--line)'}`,
    borderRadius: 20, padding: 18, marginBottom: 12,
  }}>{children}</div>
);

/* ── A curva da automaticidade ──────────────────────────────────────────
   O gráfico que explica metade do programa: rendimentos decrescentes. As
   primeiras repetições produzem os maiores ganhos, e existe um ponto a partir
   do qual repetir mais não deixa o comportamento mais automático. */
const CurvaAutomaticidade: React.FC<{ dia: number | null }> = ({ dia }) => {
  const L = 300, A = 108;
  const f = (x: number) => 1 - Math.exp(-3.1 * x);           // assíntota
  const pontos = Array.from({ length: 61 }, (_, i) => {
    const t = i / 60;
    return `${8 + t * (L - 16)},${A - 12 - f(t) * (A - 30)}`;
  }).join(' ');
  const tAtual = dia ? Math.min(1, dia / 67) : null;

  return (
    <svg viewBox={`0 0 ${L} ${A}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      <defs>
        <linearGradient id="habCurva" x1="0" x2="1">
          <stop offset="0%" stopColor="var(--ember)" stopOpacity="0.9" />
          <stop offset="100%" stopColor="var(--ember)" stopOpacity="0.25" />
        </linearGradient>
      </defs>
      <line x1="8" y1={A - 12} x2={L - 8} y2={A - 12} stroke="var(--line-strong)" strokeWidth="1" />
      <polyline points={pontos} fill="none" stroke="url(#habCurva)" strokeWidth="2.5" strokeLinecap="round" />
      {tAtual !== null && (
        <>
          <line x1={8 + tAtual * (L - 16)} y1={A - 12} x2={8 + tAtual * (L - 16)} y2={A - 12 - f(tAtual) * (A - 30)}
            stroke="var(--ember)" strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />
          <circle cx={8 + tAtual * (L - 16)} cy={A - 12 - f(tAtual) * (A - 30)} r="4.5" fill="var(--ember)" />
        </>
      )}
      <text x="10" y={A - 1} fontSize="8" fill="var(--ink-4)" fontFamily="Geist Mono, monospace">DIA 1</text>
      <text x={L - 34} y={A - 1} fontSize="8" fill="var(--ink-4)" fontFamily="Geist Mono, monospace">DIA 67</text>
    </svg>
  );
};

/* ── Comparação de efeito ─────────────────────────────────────────────── */
const Barra: React.FC<{ rotulo: string; valor: number; max: number; destaque?: boolean }> = ({ rotulo, valor, max, destaque }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: destaque ? 'var(--ink)' : 'var(--ink-3)', marginBottom: 5 }}>
      <span>{rotulo}</span>
      <span className="mono" style={{ color: destaque ? 'var(--ember)' : 'var(--ink-3)' }}>d = {valor.toFixed(2).replace('.', ',')}</span>
    </div>
    <div style={{ height: 6, borderRadius: 999, background: 'var(--paper-3)', overflow: 'hidden' }}>
      <div style={{
        width: `${(valor / max) * 100}%`, height: '100%', borderRadius: 999,
        background: destaque ? 'var(--ember)' : 'var(--ink-4)',
      }} />
    </div>
  </div>
);

/* ── O decaimento dos lembretes ───────────────────────────────────────── */
const Decaimento: React.FC = () => {
  const faixas = [
    { r: 'Dias 1–21', n: 7, t: 'todo dia' },
    { r: 'Dias 22–33', n: 4, t: 'dia sim, dia não' },
    { r: 'Dias 34–45', n: 2, t: '2× na semana' },
    { r: 'Dias 46–67', n: 1, t: '1× na semana' },
    { r: 'Depois', n: 0, t: 'nenhum' },
  ];
  return (
    <div>
      {faixas.map((f) => (
        <div key={f.r} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', width: 74, flexShrink: 0 }}>{f.r}</div>
          <div style={{ display: 'flex', gap: 3, flex: 1 }}>
            {Array.from({ length: 7 }, (_, i) => (
              <div key={i} style={{
                flex: 1, height: 16, borderRadius: 4,
                background: i < f.n ? 'var(--ember)' : 'var(--paper-3)',
                opacity: i < f.n ? 1 - i * 0.06 : 1,
              }} />
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', width: 84, textAlign: 'right', flexShrink: 0 }}>{f.t}</div>
        </div>
      ))}
    </div>
  );
};

/* ── A linha dos estágios ─────────────────────────────────────────────── */
const ESTAGIOS_INFO = [
  { chave: 'ignicao', n: 'Estágio 2', nome: 'A Ignição', faixa: 'dias 1 a 7', o: 'Executar uma vez e sentir que foi fácil. Nada mais. É aqui que a curva sobe mais.' },
  { chave: 'vale', n: 'Estágio 3', nome: 'O Vale', faixa: 'dias 8 a 21', o: 'Sobreviver ao primeiro lapso. Não evitá-lo — sobreviver a ele.' },
  { chave: 'transferencia', n: 'Estágio 4', nome: 'A Transferência', faixa: 'dias 22 a 45', o: 'O gatilho sai do aplicativo e vai para o mundo físico. Você para de ler porque o app lembrou e passa a ler porque sentou na poltrona.' },
  { chave: 'consolidacao', n: 'Estágio 5', nome: 'A Consolidação', faixa: 'dias 46 a 67 e além', o: 'Medir a automaticidade de verdade e entregar o marco com honestidade.' },
  { chave: 'identidade', n: 'Estágio 6', nome: 'A Identidade', faixa: 'permanente', o: 'De "estou tentando ler mais" para "eu sou alguém que lê".' },
];

export const ProgramaScreen: React.FC<{
  session: AuthSession | null;
  estado: EstadoHabito | null;
  onVoltar: () => void;
  onMudou: () => void;
  onEditarContrato?: () => void;
}> = ({ session, estado, onVoltar, onMudou, onEditarContrato }) => {
  const [lembretes, setLembretes] = useState(estado?.inscricao?.lembretes !== 'off');
  const [fila, setFila] = useState<ItemDaFila[]>([]);
  const [maxFila, setMaxFila] = useState(2);
  const [addAberto, setAddAberto] = useState(false);

  const carregarFila = React.useCallback(async () => {
    try { const r = await habitoApi.fila(session); setFila(r.fila); setMaxFila(r.maximo); }
    catch { /* a fila é acessório: falhar aqui não quebra a tela */ }
  }, [session]);
  useEffect(() => { carregarFila(); }, [carregarFila]);
  const [salvando, setSalvando] = useState(false);
  const atual = estado?.estagio?.chave;

  async function alternarLembretes(v: boolean) {
    setLembretes(v);
    setSalvando(true);
    try { await habitoApi.ajustes(session, { lembretes: v ? 'auto' : 'off' }); onMudou(); }
    catch { setLembretes(!v); }
    finally { setSalvando(false); }
  }

  return (
    <div className="scroll" style={{ paddingBottom: 120 }}>
      <TopBar title="O Programa" onBack={onVoltar} />

      {/* ── Abertura ───────────────────────────────────────────────── */}
      <div style={{ padding: '10px 20px 0' }}>
        <h2 style={{ ...h2, fontSize: 32 }}>
          Por que este programa<br />é <i>desse jeito</i>.
        </h2>
        <p style={p}>
          Cada regra daqui saiu de um estudo, e algumas contrariam o que a maioria
          dos aplicativos faz. Esta página existe para você conferir — inclusive o
          que a evidência <b>não</b> sustenta.
        </p>
      </div>

      {/* ── Onde você está ─────────────────────────────────────────── */}
      {estado?.dia && (
        <Secao titulo="Onde você está" sobre="Sua posição">
          <Cartao>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <div style={{ fontFamily: serif, fontSize: 30, color: 'var(--ink)' }}>Dia {estado.dia}</div>
              <div className="mono" style={{ ...eyebrow, color: 'var(--ember)' }}>{estado.estagio.nome}</div>
            </div>
            <CurvaAutomaticidade dia={estado.dia} />
            <p style={{ ...p, fontSize: 13, margin: '10px 0 0', color: 'var(--ink-3)' }}>
              A automaticidade não cresce em linha reta: sobe rápido no começo e vai
              achatando. Sentir que "não está evoluindo mais" lá pelo dia trinta é o
              comportamento normal da curva, não fracasso.
            </p>
          </Cartao>
        </Secao>
      )}

      {/* ── Os estágios ────────────────────────────────────────────── */}
      <Secao titulo="Os seis estágios" sobre="O caminho">
        <div style={{ position: 'relative', paddingLeft: 22 }}>
          <div style={{ position: 'absolute', left: 5, top: 8, bottom: 8, width: 1, background: 'var(--line-strong)' }} />
          {ESTAGIOS_INFO.map((e) => {
            const aqui = e.chave === atual;
            return (
              <div key={e.chave} style={{ position: 'relative', marginBottom: 20 }}>
                <div style={{
                  position: 'absolute', left: -22, top: 4, width: 11, height: 11, borderRadius: '50%',
                  background: aqui ? 'var(--ember)' : 'var(--paper-3)',
                  border: `2px solid var(--paper)`,
                  boxShadow: aqui ? '0 0 0 4px var(--ember-soft)' : 'none',
                }} />
                <div className="mono" style={{ ...eyebrow, color: aqui ? 'var(--ember)' : 'var(--ink-4)' }}>
                  {e.n} · {e.faixa}
                </div>
                <div style={{ fontFamily: serif, fontSize: 20, color: 'var(--ink)', margin: '3px 0 4px' }}>{e.nome}</div>
                <div style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink-2)' }}>{e.o}</div>
              </div>
            );
          })}
        </div>
      </Secao>

      {/* ── O contrato ─────────────────────────────────────────────── */}
      <Secao titulo="Por que o contrato tem só duas linhas" sobre="O método · 1">
        <p style={p}>
          Planos do tipo "se acontecer X, então eu faço Y" funcionam — mas o efeito
          desaba quando você detalha demais. Uma análise de <b>642 testes</b> mediu
          cada formulação:
        </p>
        <Cartao>
          <Barra rotulo="Quando + onde" valor={0.46} max={0.5} destaque />
          <Barra rotulo="Quando + onde + como + quanto tempo" valor={0.24} max={0.5} />
          <Barra rotulo="Só o horário" valor={0.25} max={0.5} />
          <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 4, lineHeight: 1.5 }}>
            Sheeran, Listrom &amp; Gollwitzer (2025), 642 testes. "d" é o tamanho do
            efeito: quanto maior, mais o plano muda o comportamento.
          </div>
        </Cartao>
        <p style={p}>
          É por isso que este app <b>nunca</b> te pergunta quantas páginas ou por
          quanto tempo dentro do contrato. Não é preguiça de formulário — pedir isso
          derrubaria o efeito quase pela metade.
        </p>
        <Cartao>
          <div className="mono" style={{ ...eyebrow, marginBottom: 8 }}>Mais três coisas que a mesma análise mostrou</div>
          {[
            ['Um plano por vez', 'com dois planos o efeito cai para 0,30; com três, para 0,07 — praticamente zero.'],
            ['Gatilho oferecido bate gatilho inventado', 'quando a pessoa inventa o próprio gatilho o efeito é 0,16. Por isso o app sugere, com um já escolhido.'],
            ['Ensaiar uma vez', 'sobe de 0,33 para 0,50. Ensaiar mais não adiciona nada — por isso o ensaio acontece uma vez só.'],
          ].map(([t, d]) => (
            <div key={t} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 600 }}>{t}</div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>{d}</div>
            </div>
          ))}
        </Cartao>
      </Secao>

      {/* ── O mínimo ───────────────────────────────────────────────── */}
      <Secao titulo="Por que uma página vence o dia" sobre="O método · 2">
        <p style={p}>
          O que forma hábito é <b>repetir no mesmo contexto</b> — mais do que a
          motivação, mais do que a intensidade, mais do que a duração da sessão. Em
          novos frequentadores de academia, o limiar para o hábito se estabelecer foi
          <b> quatro sessões por semana durante seis semanas</b>; a frequência
          importou, a duração de cada sessão não.
        </p>
        <p style={p}>
          O objetivo do dia, então, não é ler bastante: é não quebrar a cadeia. Quem
          abre o livro raramente lê só uma página — mas a promessa precisa ser
          pequena o bastante para nunca dar medo.
        </p>
        <Cartao escuro>
          <div style={{ fontSize: 13.5, lineHeight: 1.6, color: 'rgba(245,241,234,0.8)' }}>
            <b style={{ color: '#F5F1EA' }}>Lacuna honesta:</b> não existe estudo
            comparando diretamente 10 minutos sete vezes por semana contra 70 minutos
            de uma vez. "Consistência vence volume" é coerente com o modelo e apoiado
            de forma indireta — mas não foi testado assim. Não tratamos como fato
            demonstrado.
          </div>
        </Cartao>
      </Secao>

      {/* ── O lapso ────────────────────────────────────────────────── */}
      <Secao titulo="O dia que você perde custa quase nada" sobre="O método · 3">
        <Cartao escuro>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <div style={{ fontFamily: serif, fontSize: 44, color: 'var(--ember)', lineHeight: 1 }}>0,29</div>
            <div style={{ fontSize: 13.5, color: 'rgba(245,241,234,0.75)', lineHeight: 1.5 }}>
              pontos perdidos numa escala de <b style={{ color: '#F5F1EA' }}>0 a 42</b>,
              depois de uma oportunidade perdida. A diferença não foi estatisticamente
              significativa e não houve custo de longo prazo.
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: 'rgba(245,241,234,0.45)', marginTop: 12, lineHeight: 1.5 }}>
            Lally et al. (2010), 140 oportunidades perdidas entre 55 participantes. E o
            momento não importa: falhar no dia 5 não é pior que falhar no dia 50.
          </div>
        </Cartao>
        <p style={p}>
          O contador que zera na sua cara é uma ficção de produto que contradiz a
          biologia do hábito. O dano não está no lapso — está na <i>interpretação</i> dele.
          Por isso este app nunca te mostra um zero, e por isso o perdão já vem no seu
          bolso, sem você precisar pedir.
        </p>
        <p style={{ ...p, color: 'var(--ink-3)', fontSize: 13.5 }}>
          Mas o perdão é limitado de propósito: perdão ilimitado destrói o
          compromisso, que é o ingrediente ativo. E há um limite real na evidência —
          o que foi medido é uma falha isolada. Uma semana inteira perdida é outra
          coisa, e aí existe prejuízo de verdade.
        </p>
      </Secao>

      {/* ── O retorno ──────────────────────────────────────────────── */}
      <Secao titulo="A coisa mais forte que já foi medida" sobre="O método · 4">
        <Cartao>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <div>
              <div style={{ fontFamily: serif, fontSize: 32, color: 'var(--ink)', lineHeight: 1 }}>53</div>
              <div className="mono" style={{ ...eyebrow, marginTop: 3 }}>estratégias</div>
            </div>
            <div>
              <div style={{ fontFamily: serif, fontSize: 32, color: 'var(--ink)', lineHeight: 1 }}>61.293</div>
              <div className="mono" style={{ ...eyebrow, marginTop: 3 }}>pessoas</div>
            </div>
            <div style={{ flex: 1, fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink-2)' }}>
              testadas ao mesmo tempo, no maior experimento de mudança de
              comportamento já feito.
            </div>
          </div>
        </Cartao>
        <p style={p}>
          A vencedora não foi premiar quem nunca falta. Foi uma micro-recompensa para
          quem, <b>tendo faltado, aparecia na vez seguinte</b>. É por isso que a
          comemoração de quando você volta é maior aqui do que a de um dia perfeito.
        </p>
        <p style={{ ...p, color: 'var(--ink-3)', fontSize: 13.5 }}>
          Ressalva dos próprios autores: os efeitos caíram forte depois que o programa
          acabou. Recompensa externa cria adesão enquanto existe; ela não instala
          hábito sozinha. Por isso ela some conforme você avança.
        </p>
      </Secao>

      {/* ── A notificação ──────────────────────────────────────────── */}
      <Secao titulo="Por que eu vou te lembrar cada vez menos" sobre="O método · 5">
        <p style={p}>
          Lembretes eletrônicos aumentam a repetição, mas <b>impedem a
          automaticidade</b> e o aprendizado da associação entre o contexto e a
          resposta. Um app que notifica para sempre mantém você dependente e nunca
          entrega o hábito que prometeu.
        </p>
        <Cartao><Decaimento /></Cartao>
        <p style={{ ...p, fontSize: 13.5, color: 'var(--ink-3)' }}>
          E o texto muda junto: a partir do dia 22 ele para de falar do aplicativo e
          passa a falar do lugar, do objeto e da hora. O sucesso aqui é deixar de ser
          necessário no momento de ler.
        </p>
      </Secao>

      {/* ── A medida ───────────────────────────────────────────────── */}
      <Secao titulo="Como saber se pegou de verdade" sobre="O método · 6">
        <p style={p}>
          Dias consecutivos medem o seu uso do app, não o seu hábito. Existe um
          questionário de quatro perguntas com validade quase idêntica à da escala
          completa de doze itens (correlação de 0,92) — e é ele que este programa usa
          para dizer, com base em medida, se ler já virou automático para você.
        </p>
        <Cartao>
          <div className="mono" style={{ ...eyebrow, marginBottom: 10 }}>Ler é algo que eu…</div>
          {['faço automaticamente', 'faço sem precisar lembrar conscientemente', 'faço sem pensar', 'começo antes de perceber que comecei'].map((t, i) => (
            <div key={t} style={{ display: 'flex', gap: 10, marginBottom: 7, alignItems: 'baseline' }}>
              <span className="mono" style={{ fontSize: 10, color: 'var(--ember)' }}>0{i + 1}</span>
              <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>{t}</span>
            </div>
          ))}
        </Cartao>
      </Secao>

      {/* ── A honestidade ──────────────────────────────────────────── */}
      <Secao titulo="O que ninguém pode te prometer" sobre="A promessa">
        <Cartao escuro>
          <p style={{ fontSize: 14.5, lineHeight: 1.62, color: 'rgba(245,241,234,0.82)', margin: 0 }}>
            Sessenta e sete dias é um <b style={{ color: '#F5F1EA' }}>marco defensável</b>:
            quatro estudos independentes acharam medianas entre 59 e 78 dias. Mas a
            faixa real vai de <b style={{ color: '#F5F1EA' }}>18 a 254 dias</b>, e
            metade das pessoas do estudo original não formou o hábito nem em 84 dias.
          </p>
          <p style={{ fontSize: 14.5, lineHeight: 1.62, color: 'rgba(245,241,234,0.82)', margin: '12px 0 0' }}>
            Então: ninguém aqui vai te prometer o hábito <i>no</i> dia 67. O que dá
            para prometer é o processo — e que o programa continua com você depois disso.
          </p>
        </Cartao>
        <p style={{ ...p, marginTop: 12 }}>
          O número que vale a pena guardar é outro: <b>dez minutos por dia</b> colocam
          um leitor do percentil 30 para além do percentil 70.
        </p>
      </Secao>

      {/* ── Os mitos ───────────────────────────────────────────────── */}
      <Secao titulo="O que a gente jogou fora" sobre="Mitos derrubados">
        {[
          ['"21 dias para criar um hábito"', 'Veio de um cirurgião plástico em 1960, falando de quanto tempo pacientes levavam para se acostumar com a própria aparência. Nunca foi um estudo sobre hábitos.'],
          ['"d = 0,65" para planos do tipo se-então', 'O número mais citado do campo foi corrigido pelos próprios autores: com 642 testes e correção de viés de publicação, cai para 0,15. O efeito é real, mas pequeno.'],
          ['"Nunca falhe duas vezes seguidas"', 'Regra popular, sem teste que a sustente.'],
          ['"Ancorar numa rotina é melhor que marcar um horário"', 'Dois estudos compararam diretamente e não acharam diferença nenhuma. Escolha o que for mais confiável na sua vida.'],
          ['"Proteção de sequência reduz abandono em 21%"', 'Número de material de venda de fornecedor, não de estudo.'],
        ].map(([t, d]) => (
          <Cartao key={t}>
            <div style={{ display: 'flex', gap: 10 }}>
              <Icon name="close" size={15} color="#E2574C" style={{ marginTop: 3, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 14.5, color: 'var(--ink)', fontWeight: 600, marginBottom: 4 }}>{t}</div>
                <div style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink-2)' }}>{d}</div>
              </div>
            </div>
          </Cartao>
        ))}
      </Secao>

      {/* ── Ajustes ────────────────────────────────────────────────── */}
      {estado?.inscricao && (
        <Secao titulo="Seus ajustes" sobre="Configuração">
          {estado.contrato && (
            <Cartao>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                <div className="mono" style={eyebrow}>Seu contrato</div>
                {onEditarContrato && (
                  <button onClick={onEditarContrato} style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: 'var(--ember)', fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 5,
                  }}>
                    <Icon name="pen" size={12} color="var(--ember)" /> Trocar
                  </button>
                )}
              </div>
              <div style={{ fontFamily: serif, fontSize: 19, lineHeight: 1.45, color: 'var(--ink)' }}>
                {estado.contrato.quandoKind === 'horario' ? 'Todo dia ' : 'Assim que eu '}
                <span style={{ color: 'var(--ember)' }}>{estado.contrato.quando.replace(/^assim que eu /, '')}</span>
                , eu leio <span style={{ color: 'var(--ember)' }}>{estado.contrato.onde}</span>.
              </div>
            </Cartao>
          )}
          <Cartao>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, color: 'var(--ink)', fontWeight: 600 }}>Lembretes</div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5, marginTop: 3 }}>
                  {lembretes
                    ? 'Ligados, com a cadência decaindo por estágio. Depois do dia 67 eles param sozinhos.'
                    : 'Desligados. Isso é uma escolha legítima — o objetivo do programa é justamente você não precisar deles.'}
                </div>
              </div>
              <Toggle on={lembretes} onChange={() => !salvando && alternarLembretes(!lembretes)} />
            </div>
          </Cartao>
          <Cartao>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, color: 'var(--ink)', fontWeight: 600, marginBottom: 3 }}>
                  O mínimo que vence o dia
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                  {estado.inscricao.minimoPaginas === 1
                    ? 'Uma página. Fica fora do contrato de propósito — e é pequeno de propósito.'
                    : `${estado.inscricao.minimoPaginas} páginas.`}
                </div>
              </div>
              {onEditarContrato && (
                <button onClick={onEditarContrato} style={{
                  background: 'none', border: '1px solid var(--line-strong)', borderRadius: 999,
                  padding: '8px 14px', cursor: 'pointer', color: 'var(--ink-2)', fontSize: 12.5, flexShrink: 0,
                }}>Mudar</button>
              )}
            </div>
          </Cartao>
        </Secao>
      )}

      {/* ── A fila ─────────────────────────────────────────────────── */}
      {estado?.inscricao && estado.inscricao.status !== 'dispensado' && (
        <Secao titulo="O próximo livro" sobre="A fila">
          <p style={p}>
            Em 67 dias você termina um livro. A fila existe para que, no dia em que
            isso acontecer, <b>não haja escolha a fazer</b> — o próximo já entra
            sozinho. Não é meta de quantidade: é a regra de zero decisões no momento
            de ler, aplicada ao dia da virada.
          </p>
          {fila.map((f) => (
            <Cartao key={f.id}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <BookCover book={{ title: f.titulo, coverUrl: f.capaUrl, coverHue: f.capaHue, coverStyle: f.capaEstilo } as any} width={40} height={60} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, color: 'var(--ink)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.titulo}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{f.autor}</div>
                </div>
                <button onClick={async () => {
                  await habitoApi.removerDaFila(session, f.id);
                  setFila((v) => v.filter((x) => x.id !== f.id));
                }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}>
                  <Icon name="trash" size={15} color="var(--ink-4)" />
                </button>
              </div>
            </Cartao>
          ))}

          {fila.length < maxFila ? (
            addAberto ? (
              <Cartao>
                <EscolherLivro
                  session={session}
                  paraFila
                  onPronto={async () => { setAddAberto(false); await carregarFila(); }}
                  onCancelar={() => setAddAberto(false)}
                />
              </Cartao>
            ) : (
              <button onClick={() => setAddAberto(true)} style={{
                width: '100%', padding: '14px 16px', borderRadius: 16, cursor: 'pointer',
                border: '1px dashed var(--line-strong)', background: 'transparent',
                color: 'var(--ink-2)', fontSize: 14, display: 'flex', alignItems: 'center', gap: 9,
              }}>
                <Icon name="plus" size={15} color="var(--ember)" />
                {fila.length ? 'Adicionar mais um' : 'Escolher o próximo'}
              </button>
            )
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
              A fila guarda {maxFila}. Mais que isso vira lista de intenção — e lista
              de intenção reduz a execução em vez de aumentar.
            </div>
          )}
        </Secao>
      )}

      {/* ── Fontes ─────────────────────────────────────────────────── */}
      <Secao titulo="De onde veio cada coisa" sobre="Fontes">
        <div style={{ fontSize: 12.5, lineHeight: 1.75, color: 'var(--ink-3)' }}>
          Lally, van Jaarsveld, Potts &amp; Wardle (2010), <i>European Journal of Social
          Psychology</i> — formação de hábito no mundo real, 96 participantes.<br />
          Sheeran, Listrom &amp; Gollwitzer (2025), <i>European Review of Social
          Psychology</i> — meta-análise de 642 testes de planos se-então.<br />
          Milkman et al. (2021), <i>Nature</i> 600 — megaestudo com 61.293 pessoas.<br />
          Kaushal &amp; Rhodes (2015), <i>Journal of Behavioral Medicine</i> — limiar de
          quatro sessões por semana.<br />
          Wood &amp; Rünger (2016), <i>Annual Review of Psychology</i> — lembretes,
          deliberação e automaticidade.<br />
          Gardner, Abraham, Lally &amp; de Bruijn (2012) — o índice de automaticidade de
          quatro itens.<br />
          Adams &amp; Leary (2007), <i>Journal of Social and Clinical Psychology</i> —
          autocompaixão contra o descontrole depois do deslize.<br />
          Gollwitzer, Sheeran, Michalski &amp; Seifert (2009), <i>Psychological Science</i>
          — quando anunciar a intenção reduz a execução.<br />
          Bains et al. (2025), <i>Quarterly Journal of Experimental Psychology</i> —
          escolher entre opções aumenta o prazer da leitura.<br />
          Keller et al. (2021) e Singh et al. (2024) — rotina contra horário fixo.<br />
          Bone et al. (2025), <i>iScience</i>, e Retratos da Leitura no Brasil, 6ª edição
          (Ipec, 2024) — o diagnóstico de que o problema é a entrada.
        </div>
      </Secao>
    </div>
  );
};

export default ProgramaScreen;
